import React from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import QuizRoundedIcon from "@mui/icons-material/QuizRounded";
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded";
import { Localize } from "../../../app/lang/langs";
import { useNavigate } from "react-router-dom";

export default function VcompParticipateNotification() {
    const navigate = useNavigate();
    const vcompParticipateNotification = useSelector(
        (state) => state.dashboard.vcompParticipateNotification
    );
    const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);

    const navToVcompProject = (rowData) => {
        const encodedLabel = encodeURIComponent(rowData.project_name);
        navigate(`/vcomp/${selectedTeamsIds[0]}/0/${encodedLabel}`);
    };

    if (!Array.isArray(vcompParticipateNotification) || vcompParticipateNotification.length === 0) {
        return (
            <Typography
                variant="body2"
                sx={{
                    textAlign: "center",
                    py: 3,
                    color: "rgba(0, 40, 128, 0.4)",
                    fontStyle: "italic",
                    fontSize: "0.9rem",
                }}
            >
                {Localize("NO_NOTIFICATIONS") || "Aucune notification"}
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5}>
            {vcompParticipateNotification.map((row, index) => {
                const isAnswered =
                    row.answered === true || row.answered === 1 || row.answered === "1";
                const hasMcq =
                    row.active_mcq === "1" || row.active_mcq === 1 || row.active_mcq === true;

                return (
                    <Fade in timeout={300 + index * 80} key={row.project_name + "-" + index}>
                        <Card
                            elevation={0}
                            onClick={() => navToVcompProject(row)}
                            sx={{
                                borderRadius: "14px",
                                border: "1px solid rgba(227, 227, 227, 0.45)",
                                borderLeft: isAnswered
                                    ? "1px solid rgba(227, 227, 227, 0.45)"
                                    : "4px solid rgba(255, 193, 7, 0.7)",
                                background: isAnswered
                                    ? "rgba(255, 255, 255, 0.6)"
                                    : "rgba(255, 193, 7, 0.06)",
                                transition: "all 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                cursor: "pointer",
                                "&:active": { transform: "scale(0.985)" },
                                overflow: "visible",
                            }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                {/* Top row: project name + session badge */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 1 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.9rem",
                                            color: "rgba(15, 23, 42, 0.88)",
                                            lineHeight: 1.3,
                                            flex: 1,
                                            mr: 1,
                                        }}
                                    >
                                        {row.project_name}
                                    </Typography>
                                    <Chip
                                        label={`S${row.session_number ?? "-"}`}
                                        size="small"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            height: 24,
                                            bgcolor: "rgba(100, 116, 139, 0.1)",
                                            color: "rgba(51, 65, 85, 0.8)",
                                            borderRadius: "8px",
                                        }}
                                    />
                                </Stack>

                                {/* Date row */}
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    sx={{ mb: 1.5 }}
                                >
                                    <CalendarTodayRoundedIcon
                                        sx={{
                                            fontSize: 14,
                                            color: "rgba(255, 144, 21, 0.7)",
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "rgba(0, 40, 128, 0.6)",
                                            fontSize: "0.8rem",
                                        }}
                                    >
                                        {row.start_date_raw || "-"}
                                    </Typography>
                                </Stack>

                                {/* Chips row: answered + mcq */}
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        icon={
                                            isAnswered ? (
                                                <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                                            ) : (
                                                <AccessTimeRoundedIcon sx={{ fontSize: 16 }} />
                                            )
                                        }
                                        label={
                                            isAnswered
                                                ? Localize("ANSWERED") || "Répondu"
                                                : Localize("PENDING") || "En attente"
                                        }
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "0.78rem",
                                            height: 28,
                                            borderRadius: "20px",
                                            bgcolor: isAnswered
                                                ? "rgba(34, 197, 94, 0.12)"
                                                : "rgba(245, 158, 11, 0.12)",
                                            color: isAnswered
                                                ? "rgb(22, 163, 74)"
                                                : "rgb(217, 119, 6)",
                                            "& .MuiChip-icon": {
                                                color: "inherit",
                                            },
                                        }}
                                    />
                                    <Chip
                                        icon={
                                            hasMcq ? (
                                                <QuizRoundedIcon sx={{ fontSize: 15 }} />
                                            ) : (
                                                <RemoveCircleOutlineRoundedIcon
                                                    sx={{ fontSize: 15 }}
                                                />
                                            )
                                        }
                                        label={
                                            hasMcq
                                                ? Localize("MCQ_ACTIVE") || "QCM"
                                                : Localize("NONE") || "Aucun"
                                        }
                                        size="small"
                                        sx={{
                                            fontWeight: 500,
                                            fontSize: "0.78rem",
                                            height: 28,
                                            borderRadius: "20px",
                                            bgcolor: hasMcq
                                                ? "rgba(59, 130, 246, 0.1)"
                                                : "rgba(100, 116, 139, 0.08)",
                                            color: hasMcq
                                                ? "rgb(37, 99, 235)"
                                                : "rgba(100, 116, 139, 0.7)",
                                            "& .MuiChip-icon": {
                                                color: "inherit",
                                            },
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Card>
                    </Fade>
                );
            })}
        </Stack>
    );
}
